import { useState, useContext, useEffect, useCallback } from 'react';
import { 
  Box,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  Alert,
} from '@mui/material';
import { DeleteModal } from 'components/FAB/BulkEditModals'
import { Spinner } from 'components/Icons';
import { Tracking } from 'components/Tracking';
import { AuthContext, capitalize, force_logout } from 'components';
import { 
  Brand, 
  Category, 
  PriceInfo, 
  Photos, 
  PhotoUpload, 
  ColorsField, 
  DeliveryField,
  StyleNameField,
  OrderNumberField,
  StyleNumberField,
  NoteField,
  CommitedReceivedField,
} from './fields';

import heic2any from "heic2any";
import moment from 'moment';

import { getStorageValue } from 'hooks/use-local-storage'
import { getDatabase, ref, set, push, onValue, get } from "firebase/database";
import { firebase_data_url } from 'config';

import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { getStorage, ref as storage_ref, uploadBytes } from "firebase/storage";

const db = getDatabase()


const ANOTHER_USER = 'Another user'
const PDMDialog = (props) => {
  const { onCancel, onSubmit, open, data, alternativeText } = props;
  const { currentUser } = useContext(AuthContext);
  const [ user, setUser ] = useState(ANOTHER_USER);

  useEffect(() => {
    if (currentUser.uid === data.lastModifiedByUserId) {
      setUser(ANOTHER_USER)
    } else {
      let user_ref = ref(db, `users/${data.lastModifiedByUserId}`);
      get(user_ref).then((snapshot) => {
        const data = snapshot.val();
        if (data?.userFirstName && data?.userLastName) {
          setUser(`${capitalize(data?.userFirstName?.toLowerCase())} ${capitalize(data?.userLastName?.toLowerCase())}`)
        } else if (data?.userFirstName) {
          setUser(`${capitalize(data?.userFirstName?.toLowerCase())}`)
        } else if (data?.userLastName) {
          setUser(`${capitalize(data?.userLastName?.toLowerCase())}`)
        } else if (data?.userEmail) {
          setUser(data?.userEmail)
        } else {
          setUser(ANOTHER_USER)
        }
      }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
    }
  }, [data])

  return <Dialog open={open}>
    <DialogContent sx={{ maxWidth: 450 }}>
      {alternativeText ? (
        <p>To avoid overwriting your colleague’s changes, you must update before you can save.</p>
      ) : (
        <p>{user} just made a change to this product.</p>
      )}
      <p>Click Update to get the latest information.</p>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
      <Button variant="contained" color="secondary" onClick={onSubmit}>Update</Button>
    </DialogActions>
  </Dialog>
}


export const Product = (props) => {
  const { product: original_product, onCreate, clearSelection, onClose, open } = props;
  const [ loading, setLoading ] = useState(false);
  const [ firebaseProductChanged, setFirebaseProductChanged ] = useState(false);
  const [ renderTime, setRenderTime ] = useState(new Date().getTime())
  const [ saving, setSaving ] = useState(false);
  const [ edited, setEdited ] = useState(false);
  const [ PDMDialogAlternativeText, setPDMDialogAlternativeText ] = useState(false);
  const [ PDMDialogOpen, setPDMDialogOpen ] = useState(false);
  const [ photos, setPhotos ] = useState([]);
  const [ tags, setTags ] = useState({});
  const [ product, setProduct ] = useState(JSON.parse(JSON.stringify(original_product)));
  const { selectedStore, selectedSeason, updateItem, accountId, notify } = useContext(AuthContext);
  let showDebugInfo = process.env.REACT_APP_ENV !== 'prod';

  if (getStorageValue('debug') == '1') {
    showDebugInfo = true;
  }

  
  const onDrop = useCallback(acceptedFiles => {
    const storage = getStorage();
    const upload_buffer = []

    if (photos.length + acceptedFiles.length > 4) {
      notify({ 
        message: 'Products can only have 4 images attached',
        variant: 'warning'
      })
    } else {
      for (const f of acceptedFiles) {
        let name = uuidv4();
        const storageRef = storage_ref(storage, `${accountId}/${name}.jpg`);
        let metadata = { name }
  
        upload_buffer.push(new Promise((resolve, reject) => {
          if (f.type === 'image/heic') {
            heic2any({
              blob: f,
              toType: 'image/jpeg'
            }).then((data) => {
              uploadBytes(storageRef, data, metadata).then((snapshot) => {
                resolve(name)
              })
            })
          } else {
            uploadBytes(storageRef, f, metadata).then((snapshot) => {
              resolve(name)
            })
          }
        }))
      }
  
      Promise.all(upload_buffer).then((values) => {
        console.log(photos, values)
        setPhotos([...photos, ...values].filter(p => p !== 'missing_image'))
      })
    }

  }, [photos])

  const {getRootProps, getInputProps, isDragActive, open: open_upload_file_selector} = useDropzone({
    onDrop,
    noClick: true
  })
  

  // const handleOpen = () => setOpen(true);
  const handleClose = () => {
    // setOpen(false);
    if (onClose) {
      onClose()
    }

    setSaving(false);
  }

  const handleFirebaseUpdate = () => {
    setLoading(true)
    setPDMDialogOpen(false)
    setRenderTime(new Date().getTime())
    setProduct(firebaseProductChanged)
    setPhotos(firebaseProductChanged?.photos || [])
    setTags(firebaseProductChanged?.tag || {})
    setFirebaseProductChanged(false)
    setTimeout(() => {
      setEdited(false)
      setLoading(false) 
    }, 5)
  }

  useEffect(() => {
    if (open && renderTime < new Date().getTime() - 1000) {
      setEdited(true)
    }
  }, [product])
  
  
  useEffect(() => {
    if (!edited && firebaseProductChanged) {
      handleFirebaseUpdate()
    } else if (firebaseProductChanged) {
      setPDMDialogOpen(true)
    }
  }, [firebaseProductChanged])

  useEffect(() => {
    setEdited(false)
    setFirebaseProductChanged(false)
    setLoading(true)

    if (original_product.create && !original_product.id) {
      original_product.id = push(ref(db, `items/${selectedStore}/${selectedSeason}`)).key
    }

    if (open) {
      console.log('PDP:', product);
      
      let counter = 0
      return onValue(ref(db, `items/${selectedStore}/${selectedSeason}/${original_product.id}`), (snapshot) => { 
        let data = snapshot.val();

        if (counter === 0) {
          setProduct(data)
          setPhotos(data?.photos || [])
          setTags(data?.tag || {})
          setLoading(false)
        } else {
          setFirebaseProductChanged(data)
        }
        counter++
      });
    }
  }, [open])

  useEffect(() => { 
    if (original_product && open) {
      Tracking.track('Portal - Product details', { product_id: product.id }) 
    }
  }, [])
  
  useEffect(() => { 
    setEdited(false)
    if (open) {
      setRenderTime(new Date().getTime())

      if (product.create && clearSelection) {
        clearSelection()
      }
    }
  }, [open])
  
  if (!original_product) return;

  const handleSave = () => {
    console.log('original product', original_product)
    console.log('updated product', product)
    setSaving(true);

    updateItem(
      original_product,
      {
        ...product,
        photos,
        tag: tags
      },
      () => {
        if (onCreate) {
          onCreate()
        }
        handleClose()
      }
    )
  }

  return (
    <div>
      <div>{ props.button ? (props.button) : ("Open modal") }</div>
        <Dialog {...getRootProps()} fullWidth maxWidth={'lg'} open={open} onClose={handleClose}>
          <DialogContent>
            <PDMDialog
              data={product}
              onCancel={() => { setPDMDialogOpen(false); setPDMDialogAlternativeText(false); }}
              onSubmit={handleFirebaseUpdate}
              open={!saving && PDMDialogOpen}
              alternativeText={PDMDialogAlternativeText}
            />
            { isDragActive ? (
              <Box sx={{
                background: 'gray',
                opacity: 0.9,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid white',
                color: 'white',
                fontSize: 16
              }}>
                Drop files here...
              </Box>
            ) : null}
            
            <Grid container rowSpacing={3} spacing={1}>
              <Grid item xs={4}>
                {photos.length < 4 ? (<PhotoUpload open_upload_file_selector={open_upload_file_selector} upload_input_props={getInputProps} product={product} photos={photos} onChange={(v) => { setProduct({...product, photos: v}); setPhotos(v) }} />) : null }
                <Photos product={product} tags={tags} photos={photos} onChange={(v) => { 
                  if (v.photos) { setPhotos(v.photos) } 
                  if (v.tag) { setTags(v.tag) } 
                }} />
              </Grid>
              {!loading ? (
                <Grid item xs={6}>
                  {showDebugInfo && (
                    <Grid container rowSpacing={0} spacing={0}>
                      <Grid item xs={12}>store: {selectedStore}</Grid>
                      <Grid item xs={12}>season: {selectedSeason}</Grid>
                      <Grid item xs={12}>product: {original_product.id}</Grid>
                      <Grid item xs={12}>creation_date: {original_product.creationDate} | {moment.unix(original_product.creationDate).format("YYYY-MM-DD HH:mm:ss")}</Grid>
                      <hr/>
                    </Grid>
                  )}
                  <Grid container rowSpacing={3} spacing={1}>
                    {showDebugInfo && (
                      <Grid item xs={6}>
                        <a href={`${firebase_data_url}/~2Fitems/${selectedStore}/${selectedSeason}/${original_product.id}`} rel="noreferrer" target="_blank">Open in firebase</a>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Brand brand={product.brand} onChange={(value) => { 
                        if (value.create) {
                          // Stupid time based workaround due to re-rendering of new brand
                          // TODO: Definitely review this. The data becomes out of sync due to re-render of new brand
                          ((p) => {
                            setTimeout(() => {
                              setProduct(p) 
                            }, 2000)
                          })({...product, brand: value.id})
                        }
                        setProduct({...product, brand: value.id}) 
                      }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Category category={product.categoryId} onChange={(value) => { setProduct({...product, ...value}) }} />
                    </Grid>
                    <Grid item xs={12}>
                      <PriceInfo priceInfo={product.priceInfo} onChange={(v) => { 
                        setProduct({...product, priceInfo: v}) 
                      }} />
                    </Grid>
                    <Grid item xs={12}>
                      <ColorsField colors={product.color} onChange={(v) => { setProduct({...product, color: v}) }} />
                    </Grid>
                    <Grid item xs={12}>
                      <DeliveryField
                        product={original_product}
                        onChange={(v) => { 
                          const [ start, end ] = v
                          let newDates = {}

                          // Avoid nested update loop due to render change validation
                          if (open && renderTime < new Date().getTime() - 1000 && start > 0 && (end === null || end > 0)) {
                            if (start) { newDates.deliveryStart = start.unix() }
                            if (end) { newDates.deliveryEndDate = end.unix() }
                            
                            setProduct({...product, ...newDates})
                          }
                        }} 
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StyleNameField original_value={product.styleName} onChange={(v) => { setProduct({...product, styleName: v}) }} />
                    </Grid>
                    <Grid item xs={6}>
                      <StyleNumberField original_value={product.styleNumber} onChange={(v) => { setProduct({...product, styleNumber: v}) }} />
                    </Grid>
                    <Grid item xs={6}>
                      <OrderNumberField original_value={product.orderNumber} onChange={(v) => { setProduct({...product, orderNumber: v}) }} />
                    </Grid>
                    <Grid item xs={12}>
                      <NoteField original_value={product.note} onChange={(v) => { setProduct({...product, note: v}) }} />
                    </Grid>
                    <Grid item xs={12}>
                      <CommitedReceivedField
                        original_commited={product.orderStatus}
                        original_received={product.deliveries}
                        onChange={(v) => { 
                          const [ c, r ] = v
                          setProduct({...product, ...{
                            orderStatus: c ? 1 : 0,
                            deliveries: r ? 1 : 0
                          }})
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <Spinner/>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            {!product.hasOwnProperty('create') && (
              <DeleteModal
                renderButton={
                  <Button sx={{ marginRight: 'auto', marginLeft: '16px' }} color="error">Delete Product</Button>
                }
                items={[product]}
                selected={true}
                onDelete={handleClose}
              />
            )}
            <Button variant="outlined" color="secondary" onClick={handleClose}>Cancel</Button>
            {firebaseProductChanged && !PDMDialogOpen ? (
              <Button variant="contained" color="secondary" onClick={() => { setPDMDialogAlternativeText(true); setPDMDialogOpen(true) }}>Save</Button>
            ) : (
              <Button disabled={firebaseProductChanged} variant={edited ? "contained" : "outlined"} color="secondary" onClick={handleSave}>Save</Button>
            )}
          </DialogActions>
        </Dialog>
    </div>
  );
}